import { ReactNode } from 'react';
import styled from 'styled-components';

import { IconButton, Spinner } from '@creditornot/alchemy-react';
import * as icons from '@creditornot/alchemy-react/icons';
import { font, space } from '@creditornot/alchemy-react/tokens';
import { Modal } from '@creditornot/cbt-modal';
import { PageApiError } from 'common/components/cookbook/PageApiError';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useMatchMedia } from 'common/hooks/useMatchMedia';
import { media } from 'common/media';

import { Body } from './Body';
import { Form } from './Form';
import { Step } from './Step';

const Header = styled.div`
  display: flex;
  gap: ${space.regularXxxlarge};
  justify-content: space-between;
  padding: ${space.regularXxxlarge};
  padding-bottom: ${space.regularXxlarge};

  @media ${media.md} {
    padding: ${space.broadLarge};
    padding-bottom: ${space.regularXxlarge};
  }
`;

const Title = styled.h1`
  font: ${font.headline};
  margin: 0;
`;

type Props = {
  centeredMaxHeight?: string;
  children: ReactNode;
  error?: Error | null;
  isLoading?: boolean;
  onDismiss: VoidFunction;
  open: boolean;
  size?: 'xsmall' | 'smaller' | 'small' | 'large';
  title: string;
};

export function RuleBox({
  centeredMaxHeight,
  children,
  error,
  isLoading,
  onDismiss,
  open,
  size = 'large',
  title,
}: Props) {
  const f = useFormatMessage();

  const isMobile = useMatchMedia('maxMd');
  const maxWidth =
    size === 'xsmall'
      ? '500px'
      : size === 'smaller'
        ? '600px'
        : size === 'small'
          ? '700px'
          : '1000px';

  return (
    <Modal
      ContentComponent={null}
      onDismiss={onDismiss}
      open={open}
      sizeConfig={
        isMobile
          ? { type: 'bottomSheet', height: '100%' }
          : {
              height: centeredMaxHeight ? 'auto' : '740px',
              maxHeight: centeredMaxHeight ?? '740px',
              maxWidth,
              type: 'centered',
              width: '100%',
            }
      }
      styleParams={{ borderRadius: isMobile ? '24px' : '32px' }}
    >
      {isLoading ? (
        <Spinner
          aria-label={f('common.loading')}
          role="status"
          size="large"
          style={{ margin: 'auto' }}
        />
      ) : (
        <>
          <Header>
            <Title>{title}</Title>
            <IconButton
              aria-label={f('common.close_dialog')}
              icon={icons.crossSmall}
              onClick={onDismiss}
              size="xSmall"
              variant="secondaryNeutral"
            />
          </Header>
          {error ? <PageApiError error={error} /> : children}
        </>
      )}
    </Modal>
  );
}

RuleBox.Form = Form;
RuleBox.Step = Step;

// To be used for RuleBox dialogs that don't contain a form
RuleBox.Body = Body;
